/**
 * Eastern-time date logic for tasks and ticket reminders (plan
 * plans/2026-08-08-reminders-and-tasks.md A11 + A14).
 *
 * Both rules are anchored to America/New_York, not the browser's
 * timezone, because that is where the backend computes ``fire_at``
 * and where Greg's "overdue" day rolls over:
 *   - a task is overdue when due_date < today in Eastern time and the
 *     task is still open;
 *   - a reminder day part for TODAY is not offered once its Eastern
 *     firing hour has passed (morning 7am, afternoon 1pm, evening
 *     4pm) - the server 422s those; this module is the client-side
 *     filter.
 *
 * Pure functions over an injectable ``now`` so the rules are unit
 * testable without freezing the clock.
 */
import type { ReminderDayPart } from "@/types/api";

/** Eastern firing hour per day part. Mirrors the backend's day-part
 * hour map (reminder_service): morning=07:00, afternoon=13:00,
 * evening=16:00, DST handled by the IANA zone. */
export const DAY_PART_HOURS_ET: Record<ReminderDayPart, number> = {
  morning: 7,
  afternoon: 13,
  evening: 16,
};

/** Select options in firing order. Labels are Title Case to match the
 * other Properties-card selects. */
export const DAY_PART_OPTIONS: { value: ReminderDayPart; label: string }[] = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
];

/** The current wall clock in America/New_York, as the pieces the two
 * rules need: the local date ("YYYY-MM-DD") and the 0-23 hour. en-CA
 * is the locale whose short date format IS ISO "YYYY-MM-DD", which
 * keeps the extraction free of formatToParts bookkeeping. */
export function easternNow(now: Date = new Date()): {
  date: string;
  hour: number;
} {
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      hourCycle: "h23",
    }).format(now),
  );
  return { date, hour };
}

/** Today's date in Eastern time, "YYYY-MM-DD". Feeds the date input's
 * ``min`` so past days are not selectable. */
export function easternToday(now: Date = new Date()): string {
  return easternNow(now).date;
}

/** Overdue = the due date is strictly before today in Eastern time.
 * ISO "YYYY-MM-DD" strings compare correctly as plain strings, so no
 * Date parsing (which would drag the browser timezone back in).
 * Callers only apply this to OPEN tasks - a done task is never
 * rendered overdue. */
export function isOverdue(
  dueDate: string | null,
  now: Date = new Date(),
): boolean {
  if (!dueDate) return false;
  return dueDate < easternToday(now);
}

/** Whether a day part should be disabled for the given "YYYY-MM-DD"
 * date: past dates entirely, and for today any part whose Eastern
 * firing hour has already arrived. Future dates are always fully
 * open. */
export function isDayPartDisabled(
  dayPart: ReminderDayPart,
  dateStr: string,
  now: Date = new Date(),
): boolean {
  if (!dateStr) return false;
  const { date: today, hour } = easternNow(now);
  if (dateStr > today) return false;
  if (dateStr < today) return true;
  return hour >= DAY_PART_HOURS_ET[dayPart];
}

/** The default "Remind Me" date: the next WEEKDAY after today in
 * Eastern time (Greg, 2026-08-08). From Saturday Aug 8 that is Monday
 * Aug 10; from a Thursday it is Friday; from a Friday it is Monday -
 * always strictly after today, never a Saturday or Sunday. Date-only
 * arithmetic on the Eastern date string, so the browser timezone
 * never shifts the day. */
export function nextWeekday(now: Date = new Date()): string {
  const [y, m, d] = easternToday(now).split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  do {
    dt.setDate(dt.getDate() + 1);
  } while (dt.getDay() === 0 || dt.getDay() === 6);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/** The pending-reminder display string: "Sat, Aug 8 - morning".
 * ``remind_on`` is parsed piecewise rather than via ``new Date(str)``
 * because the string form parses as UTC midnight and would render a
 * day early in any zone west of Greenwich. */
export function formatReminderLabel(
  remindOn: string,
  dayPart: ReminderDayPart,
): string {
  const [y, m, d] = remindOn.split("-").map(Number);
  const local = new Date(y, (m ?? 1) - 1, d ?? 1);
  const datePart = local.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${datePart} - ${dayPart}`;
}

/** Short due-date label for a task row: "Aug 8" (adds the year when it
 * is not the current one). Same piecewise parse as above. */
export function formatDueDate(dueDate: string): string {
  const [y, m, d] = dueDate.split("-").map(Number);
  const local = new Date(y, (m ?? 1) - 1, d ?? 1);
  const sameYear = local.getFullYear() === new Date().getFullYear();
  return local.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
